import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Search, Coins, Loader2, Plus } from "lucide-react";
import {
  adminSearchClientCredits,
  adminGrantClientCredits,
} from "@/lib/api/admin-credits-orders.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/client-credits")({
  component: ClientCreditsPage,
});

function ClientCreditsPage() {
  const search = useServerFn(adminSearchClientCredits);
  const grant = useServerFn(adminGrantClientCredits);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");

  const { data, isFetching } = useQuery({
    queryKey: ["admin-client-credits", submitted],
    queryFn: () => search({ data: { query: submitted } }),
    enabled: submitted.length >= 2,
  });

  const [drafts, setDrafts] = useState<Record<string, { amount: string; reason: string }>>({});
  const setDraft = (id: string, patch: Partial<{ amount: string; reason: string }>) =>
    setDrafts((d) => ({ ...d, [id]: { amount: d[id]?.amount ?? "", reason: d[id]?.reason ?? "", ...patch } }));

  const grantMut = useMutation({
    mutationFn: (args: { userId: string; amount: number; reason: string }) =>
      grant({ data: args }),
    onSuccess: (res, vars) => {
      toast.success(`Granted ${vars.amount} credits — new balance ${res.balance}`);
      setDrafts((d) => ({ ...d, [vars.userId]: { amount: "", reason: "" } }));
      qc.invalidateQueries({ queryKey: ["admin-client-credits"] });
    },
    onError: (e: any) => toast.error("Grant failed", { description: e?.message }),
  });

  return (
    <div className="space-y-5">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
        <h1 className="font-display text-2xl text-navy">Client credits</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Search any user and provision credits when a payment didn't reach us
          automatically. Every grant is recorded in the credit ledger.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q.trim());
        }}
        className="flex gap-2 rounded-lg border border-border bg-card p-3"
      >
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, student number or phone…"
            className="w-full rounded-md border border-input bg-background py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <button
          type="submit"
          className="rounded-md bg-navy px-4 text-sm font-medium text-navy-foreground hover:bg-navy/90"
        >
          Search
        </button>
      </form>

      {isFetching && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      )}

      {submitted && !isFetching && (data?.users.length ?? 0) === 0 && (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No users matched "{submitted}".
        </p>
      )}

      <div className="space-y-3">
        {(data?.users ?? []).map((u) => {
          const draft = drafts[u.id] ?? { amount: "", reason: "" };
          return (
            <div key={u.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-navy">
                    {u.full_name || u.email}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {u.email} · {u.student_number || "—"} · {u.phone || "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-gold/10 px-3 py-1.5 text-sm font-semibold text-navy">
                  <Coins className="h-4 w-4 text-gold" /> {u.balance} credits
                </div>
              </div>

              <div className="mt-3 grid gap-2 sm:grid-cols-[120px_1fr_auto]">
                <input
                  type="number"
                  min={1}
                  max={10000}
                  placeholder="Amount"
                  value={draft.amount}
                  onChange={(e) => setDraft(u.id, { amount: e.target.value })}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
                <input
                  placeholder="Reason (e.g. PayFast ref NDL-CR-... not provisioned)"
                  value={draft.reason}
                  onChange={(e) => setDraft(u.id, { reason: e.target.value })}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
                <button
                  type="button"
                  disabled={grantMut.isPending}
                  onClick={() => {
                    const amount = Number(draft.amount);
                    if (!Number.isInteger(amount) || amount < 1) {
                      toast.error("Enter a positive whole number");
                      return;
                    }
                    if (!draft.reason || draft.reason.trim().length < 3) {
                      toast.error("Enter a short reason (min 3 chars)");
                      return;
                    }
                    grantMut.mutate({ userId: u.id, amount, reason: draft.reason.trim() });
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
                >
                  <Plus className="h-3 w-3" /> Grant
                </button>
              </div>

              {u.recent.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    Recent ledger ({u.recent.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {u.recent.map((r: any, i: number) => (
                      <li key={i}>
                        {new Date(r.created_at).toLocaleString()} ·{" "}
                        <span className={r.delta > 0 ? "text-emerald-700" : "text-rose-700"}>
                          {r.delta > 0 ? "+" : ""}{r.delta}
                        </span>{" "}
                        · {r.reason} {r.ref ? `· ${r.ref}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
